import React, { useEffect, useRef, useState, Fragment } from 'react';
import {
    IconButton,
    MenuItem,
    MenuList,
    Paper,
    Popover,
    Tooltip,
} from '@material-ui/core';
import { Clipboard, Phone, PhoneMissed } from 'react-feather';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import useLead from 'src/hooks/useLead';
import api from 'src/api/api';
import callsApi from 'src/api/apiCalls';
import SITE_SETTINGS from 'src/constants/whiteLabel';
import { HEADERS } from 'src/constants/headers';
import { makeCall, MakeCallHandle } from 'src/shared/communications';
import { removeTwilioFromMessage } from 'src/components/Lead/Calls/utils';
import { formatPhoneByCountryCode } from 'src/modules/phones/utils/formatPhoneByCountryCode';
import { ECountryCode } from 'src/modules/forms/fields/AreaCodeTextField/components/AreaCodeSelect';
import { authorize } from 'src/utils/auth';
import { USER_LEVEL_ACCESS } from 'src/components/access';

type TCallButtonProps = {
    conversationId?: string;
    contactId?: string;
    name?: string;
    phone?: string;
    storeId?: string;
    area?: string;
    callerPhone?: string;
    storeCountryCode?: string;
    tokenOnDemand?: boolean;
    onOpenCallHistory?: () => void;
};

export const CallButton = ({
    conversationId,
    contactId,
    name,
    phone,
    storeId,
    area,
    callerPhone,
    storeCountryCode,
    tokenOnDemand = false,
    onOpenCallHistory,
}: TCallButtonProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const { callToken, generateToken } = useLead();
    const callHandleRef = useRef<MakeCallHandle | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isInCall, setIsInCall] = useState(false);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [storeAreaPhone, setStoreAreaPhone] = useState(callerPhone || '');
    const [currentStoreCountryCode, setCurrentStoreCountryCode] = useState(
        storeCountryCode || ''
    );

    const formatPhones = (phoneToFormat?: string) => {
        if (!phoneToFormat) return '';

        let normalizedPhone = phoneToFormat.replace(/\+521/g, '+52');
        normalizedPhone = normalizedPhone.replace(/^521/, '52');
        const countryCode =
            (currentStoreCountryCode as ECountryCode) || ECountryCode.Mexico;

        return formatPhoneByCountryCode(
            normalizedPhone,
            countryCode,
            countryCode
        );
    };

    useEffect(() => {
        if (storeId && !tokenOnDemand) generateToken(storeId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, tokenOnDemand]);

    useEffect(() => {
        setStoreAreaPhone(callerPhone || '');
        setCurrentStoreCountryCode(storeCountryCode || '');
    }, [callerPhone, storeCountryCode]);

    useEffect(() => {
        let isMounted = true;

        const loadStoreAreaPhone = async () => {
            if (callerPhone || !storeId || !area) return;

            try {
                const res = await api.get(`/stores/${storeId}`, HEADERS());
                const store = res.data.data;
                const storeArea = store?.areas?.find(
                    (item: { name?: string }) => item.name === area
                );

                if (!isMounted) return;

                setStoreAreaPhone(
                    storeArea?.phone || storeArea?.phoneCallerId || ''
                );
                setCurrentStoreCountryCode(store?.countryCode || '');
            } catch (error) {
                if (isMounted) setStoreAreaPhone('');
            }
        };

        loadStoreAreaPhone();

        return () => {
            isMounted = false;
        };
    }, [area, callerPhone, storeId]);

    useEffect(() => {
        return () => {
            if (callHandleRef.current) callHandleRef.current.disconnect();
        };
    }, []);

    const resetCall = () => {
        callHandleRef.current = null;
        setIsConnecting(false);
        setIsInCall(false);
    };

    const handleError = (message: string) => {
        resetCall();
        enqueueSnackbar(removeTwilioFromMessage(message), {
            variant: 'error',
        });
    };

    const hangUp = () => {
        if (callHandleRef.current) callHandleRef.current.disconnect();
        resetCall();
    };

    const startCall = async () => {
        const toNumber = formatPhones(phone);
        const formattedCallerId = formatPhones(storeAreaPhone);
        if (!storeId || !toNumber || !formattedCallerId) {
            enqueueSnackbar('No hay informacion suficiente para llamar', {
                variant: 'error',
            });
            return;
        }

        setIsConnecting(true);

        try {
            const token = tokenOnDemand
                ? (
                      await callsApi.post(
                          '/calls/token',
                          { store: storeId },
                          HEADERS()
                      )
                  ).data.token
                : callToken;

            if (!token) throw new Error('No fue posible generar el token');

            const handle = await makeCall({
                token,
                to: toNumber,
                from: formattedCallerId,
                params: {
                    user: user._id,
                    from: formattedCallerId,
                    area: area || null,
                    To: toNumber,
                    callerId: formattedCallerId,
                    warboxConversation: conversationId,
                    contact: contactId || null,
                    name: name || phone || null,
                    store: storeId,
                    client: SITE_SETTINGS.client,
                    record: 'do-not-record',
                    assignedBy: !authorize(user?.accessLevel, USER_LEVEL_ACCESS)
                        ? user._id
                        : null,
                    typeCall: 'first_call',
                    type: 'warboxConversation',
                },
                onRegistering: () => setIsConnecting(true),
                onTokenWillExpire: () => generateToken(storeId),
                onAccept: () => {
                    setIsConnecting(false);
                    setIsInCall(true);
                },
                onDisconnect: resetCall,
                onError: handleError,
            });

            callHandleRef.current = handle;
        } catch (error) {
            if (error instanceof Error) handleError(error.message);
        }
    };

    const closeOptions = () => setIsOptionsOpen(false);

    const handleMakeCall = () => {
        closeOptions();
        if (isActive) {
            hangUp();
            return;
        }
        startCall();
    };

    const handleViewCallHistory = () => {
        closeOptions();
        onOpenCallHistory?.();
    };

    const isActive = isConnecting || isInCall;
    const showOptionsMenu = Boolean(onOpenCallHistory);
    const isMakeCallDisabled = !tokenOnDemand && !callToken && !isActive;

    const handleButtonClick = () => {
        if (showOptionsMenu) {
            setIsOptionsOpen((open) => !open);
            return;
        }

        if (isActive) {
            hangUp();
            return;
        }

        startCall();
    };

    return (
        <Fragment>
            <Tooltip
                title={t(
                    showOptionsMenu
                        ? 'Chat.Calls'
                        : isActive
                          ? 'Leads.HangUp'
                          : 'Leads.MakeCall'
                )}
            >
                <span>
                    <IconButton
                        ref={buttonRef}
                        color={isActive ? 'secondary' : 'primary'}
                        onClick={handleButtonClick}
                    >
                        {isActive ? <PhoneMissed /> : <Phone />}
                    </IconButton>
                </span>
            </Tooltip>

            {showOptionsMenu && (
                <Popover
                    open={isOptionsOpen}
                    onClose={closeOptions}
                    anchorEl={buttonRef.current}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <Paper>
                        <MenuList>
                            <MenuItem
                                onClick={handleMakeCall}
                                disabled={isMakeCallDisabled}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                }}
                            >
                                {isActive ? (
                                    <PhoneMissed size={20} />
                                ) : (
                                    <Phone size={20} />
                                )}
                                {String(
                                    t(
                                        isActive
                                            ? 'Leads.HangUp'
                                            : 'Leads.MakeCall'
                                    )
                                )}
                            </MenuItem>
                            <MenuItem
                                onClick={handleViewCallHistory}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                }}
                            >
                                <Clipboard size={20} />
                                {String(t('Chat.ViewCallHistory'))}
                            </MenuItem>
                        </MenuList>
                    </Paper>
                </Popover>
            )}
        </Fragment>
    );
};
